import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon } from '@persona-id/design-tokens/icons';
import { sortBy, startCase } from 'lodash/fp';
import pluralize from 'pluralize';
import { FC, Fragment, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import InputRow from 'dashboard/components/WorkflowControlPanel/PlatformSchemaMappings/InputRow';
import { REACT_SELECT_PORTAL_ID_PREFIX } from 'dashboard/components/WorkflowControlPanel/PlatformSchemaMappings/JsonSchemaValue';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { valueTransformer } from 'dashboard/lib/workflows';
import { PlatformJsonSchema } from 'dashboard/lib/workflows/platform-object';
import { WorkflowError } from 'dashboard/models/workflow';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchema } from 'lib/json-schema';

const CONTAINER_STYLE = css`
  padding: ${Spacing[1]};
`;

const Container = styled.div`
  border-top: 1px solid ${Color.CoreSlate100};
  padding-top: ${Spacing[4]};
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Row = styled(FlexContainer)`
  justify-content: space-between;
`;

const InputName = styled.dt`
  ${paragraphMixins.mdBold}

  margin-top: ${Spacing[2]};
`;

const InputValue = styled.dd.attrs<{ hasError: boolean }>(({ hasError }) => ({
  'aria-invalid': hasError,
}))<{ hasError: boolean }>`
  ${paragraphMixins.md}
  margin-top: ${Spacing[1]};
  color: ${({ hasError }) => (hasError ? Color.TextDanger : 'inherit')};
  overflow-x: auto;
`;

export type InputConfig = {
  schema: PlatformJsonSchema;
  field: string | null;
  value: any;
  key: string;
  label: string;
  required?: boolean;
};

interface PlatformSchemaMappingsProps {
  buttonText: string;
  editable: boolean;
  erroredKeyPrefix: string;
  errors: WorkflowError[];
  inputConfigs: InputConfig[];
  jsonSchema: JsonSchema;
  title: string;
  tokensToDisplayValue: Record<string, string>;
  placeholderEmptyText?: string;
  resettable?: boolean;
  subtitle?: string;
  onUpdateInputConfigs: (value: InputConfig[]) => void;
}

const PlatformSchemaMappings: FC<PlatformSchemaMappingsProps> = ({
  buttonText,
  editable,
  erroredKeyPrefix,
  errors,
  inputConfigs,
  jsonSchema,
  title,
  tokensToDisplayValue,
  placeholderEmptyText,
  resettable = true,
  subtitle,
  onUpdateInputConfigs,
}) => {
  const vt = valueTransformer(tokensToDisplayValue);
  const sortedInputs = useMemo(() => sortBy('label', inputConfigs), [inputConfigs]);
  const configuredInputs = useMemo(
    () => sortedInputs.filter(({ field, value }) => field != null || value != null),
    [sortedInputs],
  );
  const numValuesSet = configuredInputs.length;
  const [showPanel, { toggle: togglePanel, disable: closePanel }] = useToggle(false);
  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError<`${string}.${string}`>(errors);

  const setInputConfig = useCallback(
    (newInputConfig: { value: any; field: string | null }, index: number) => {
      const newInputConfigs = Object.assign([], sortedInputs, {
        [index]: {
          ...sortedInputs[index],
          ...newInputConfig,
        },
      });
      onUpdateInputConfigs(
        newInputConfigs.filter(({ field, value }) => field != null || value != null),
      );
    },
    [sortedInputs, onUpdateInputConfigs],
  );

  return (
    <>
      <Row>
        <Paragraph>
          {numValuesSet === 0 && placeholderEmptyText
            ? placeholderEmptyText
            : `${numValuesSet} ${pluralize(buttonText.toLowerCase(), numValuesSet)} configured`}
        </Paragraph>
        <Button
          variant='basic'
          size='sm'
          width='auto'
          disabled={!editable}
          id='fields-list__toggle-edit-panel'
          data-test='fields-list__toggle-edit-panel'
          aria-label={`Edit ${buttonText}`}
          suffixIcon={<EditIcon />}
          onClick={togglePanel}
        >
          {buttonText}
        </Button>
      </Row>
      <dl role='list'>
        {configuredInputs.map(({ key, label, value, field }) => {
          return (
            <Fragment key={key}>
              <InputName>{startCase(label)}</InputName>
              <InputValue hasError={hasErrorFor(`${erroredKeyPrefix}.${key}`)}>
                {value || value === false ? String(value) : vt.getDisplayValue(field ?? '')}
              </InputValue>
              {editable && renderErrorMessageFor(`${erroredKeyPrefix}.${key}`, { narrow: true })}
            </Fragment>
          );
        })}
      </dl>
      {showPanel && (
        <MaximizablePanel
          title={title}
          subtitle={subtitle}
          outsideExceptionalIdPrefixes={[REACT_SELECT_PORTAL_ID_PREFIX]}
          containerStyleOverride={CONTAINER_STYLE}
          onOutsideClickAndEscPress={closePanel}
          onClosePanel={closePanel}
        >
          <Container>
            {sortedInputs.map(({ key, label, required, schema, value, field }, i) => (
              <Fragment key={key}>
                <InputRow
                  field={field}
                  id={key}
                  inputSchema={schema}
                  jsonSchema={jsonSchema}
                  label={label}
                  required={required}
                  resettable={resettable}
                  tokensToDisplayValue={tokensToDisplayValue}
                  value={value}
                  onInputChange={(newInputConfig: { value: any; field: string | null }) =>
                    setInputConfig(newInputConfig, i)
                  }
                />
                <Spacer height={1} />
              </Fragment>
            ))}
          </Container>
        </MaximizablePanel>
      )}
    </>
  );
};

export default PlatformSchemaMappings;
